import { ReactNode, useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Terminal,
  Bot,
  FolderOpen,
  FolderKanban,
  Plug,
  Wrench,
  Store,
  GitBranch,
  Globe,
  Brain,
  Container,
  LogOut,
  User,
  ChevronDown,
  PanelLeftClose,
  PanelLeftOpen,
  Menu,
  X,
  Settings,
  Shield,
  Server,
  Activity,
  CalendarClock,
  Rocket,
  BookOpen,
  Search,
  DollarSign,
  ClipboardList,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useAuth } from '../../hooks/useAuth';
import { useRole } from '../../hooks/useRole';
import { BRAND_SHORT } from '../../config/brand';
import cargillLogoDark from '../../assets/cargill-logo-dark.svg';
import cargillLogoLight from '../../assets/cargill-logo-light.svg';
import SproutFooter from './SproutFooter';
import ThemeToggle from './ThemeToggle';
import { useTheme } from '../../contexts/ThemeProvider';
import ReviewAlert from './ReviewAlert';
import { usePendingReviews } from '../../hooks/usePendingReviews';

const WhisperWidget = lazy(() => import('./WhisperWidget'));
const ReportAffordance = lazy(() => import('../reports/ReportAffordance'));
const NotificationBell = lazy(() => import('../reports/NotificationBell'));
// Feature #1's interrupt half. Barnyard's own bell above already surfaces mission notifications
// (same container, same `targetUserId`), so this adds toasts rather than a second bell — two
// bells with the same badge count would be a worse surface, not a richer one.
const NotificationToasts = lazy(() =>
  import('@cds/mission-tracker').then((m) => ({ default: m.NotificationToasts })),
);

interface LayoutProps {
  children: ReactNode;
}

import type { AppRole } from '../../hooks/useRole'; // `${SharedAppRole}` — single source in @cds/shared

interface NavItem {
  path: string;
  label: string;
  icon: typeof Terminal;
  minRole?: AppRole;
  external?: boolean;
}

const allNavItems: NavItem[] = [
  { path: '/', label: 'Chat', icon: Terminal },
  { path: '/projects', label: 'Projects', icon: FolderKanban, minRole: 'poweruser' },
  { path: '/agents', label: 'Agents', icon: Bot },
  { path: '/artifacts', label: 'Artifacts', icon: FolderOpen },
  { path: '/tools', label: 'Tools', icon: Wrench },
  { path: '/marketplace', label: 'Marketplace', icon: Store },
  { path: '/orchestrations', label: 'Orchestrations', icon: GitBranch },
  { path: '/missions', label: 'Missions', icon: Rocket, minRole: 'poweruser' },
  { path: '/reports', label: 'SDLC Reports', icon: ClipboardList, minRole: 'poweruser' },
  { path: '/external-agents', label: 'A2A Agents', icon: Globe },
  { path: '/skills', label: 'Skills', icon: BookOpen },
  { path: '/knowledge', label: 'Knowledge Hub', icon: Brain },
  { path: '/knowledge/review', label: 'Review Queue', icon: Shield, minRole: 'creator' },
  { path: '/workspace', label: 'Workspace', icon: Container, minRole: 'poweruser' },
  { path: '/connectors', label: 'Connectors', icon: Plug, minRole: 'poweruser' },
  { path: '/operations', label: 'Operations', icon: CalendarClock, minRole: 'poweruser' },
  { path: '/finops', label: 'FinOps', icon: DollarSign, minRole: 'poweruser' },
  { path: '/observability', label: 'Observability', icon: Activity, minRole: 'creator' },
  { path: '/mcp-tenants', label: 'MCP Tenants', icon: Server, minRole: 'creator' },
  { path: '_inspector_', label: 'MCP Inspector', icon: Search, minRole: 'creator', external: true },
  { path: '/admin', label: 'Admin', icon: Settings, minRole: 'creator' },
];

const ROLE_COLORS: Record<AppRole, string> = {
  reviewer: 'bg-surfaceAlt text-textSecondary border-border',
  creator: 'text-primary',
  poweruser: 'text-info',
  member: 'text-textSecondary',
};

const ROLE_LABELS: Record<AppRole, string> = {
  reviewer: 'Reviewer',
  creator: 'Creator',
  poweruser: 'Power User',
  member: 'Member',
};

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const { account, logout } = useAuth();
  const { role, hasRole } = useRole();
  const { total: pendingReviews } = usePendingReviews();
  const { mode } = useTheme();
  // Light page → dark-lettered logo (cargill.com default); dark page → white-lettered logo
  const cargillLogo = mode === 'dark' ? cargillLogoDark : cargillLogoLight;
  const [showUserMenu, setShowUserMenu] = useState(false);

  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem('sidebar-collapsed') === 'true';
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', String(collapsed));
  }, [collapsed]);

  // Close mobile sidebar on navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Filter nav items by role
  const navItems = useMemo(
    () => allNavItems.filter((item) => !item.minRole || hasRole(item.minRole)),
    [role],
  );

  const sidebarContent = (
    <>
      {/* Logo + collapse toggle */}
      <div className={clsx('border-b border-border flex items-center justify-between', collapsed ? 'p-3' : 'px-4 py-3')}>
        <Link to="/" className="flex items-center gap-2 min-w-0">
          <img src={cargillLogo} alt="Cargill" className={clsx('flex-shrink-0', collapsed ? 'w-8' : 'w-24')} />
          {!collapsed && <span className="text-sm font-bold text-primary truncate">{BRAND_SHORT}</span>}
        </Link>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden md:flex p-1 text-textSecondary hover:text-textPrimary rounded transition-colors flex-shrink-0"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
        </button>
      </div>

      {/* Nav items */}
      <nav className={clsx('space-y-1 flex-shrink-0', collapsed ? 'p-2' : 'p-3')}>
        {navItems.map((item) => {
          const Icon = item.icon;

          if (item.external) {
            const host = window.location.hostname;
            const inspectorUrl = host === 'anhdigital.ai'
              ? 'https://inspector.anhdigital.ai'
              : host.startsWith('dev.')
                ? 'https://inspector.dev.cargillai.com'
                : 'https://inspector.api.cargillai.com';
            return (
              <a
                key={item.path}
                href={inspectorUrl}
                target="_blank"
                rel="noopener noreferrer"
                title={collapsed ? item.label : undefined}
                className={clsx(
                  'flex items-center gap-3 rounded-lg transition-colors',
                  collapsed ? 'justify-center p-2.5' : 'px-3 py-2',
                  'text-textBody hover:bg-surfaceHover hover:text-textPrimary'
                )}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!collapsed && <span className="text-sm">{item.label}</span>}
              </a>
            );
          }

          const isActive = item.path === '/'
            ? location.pathname === '/'
            : location.pathname.startsWith(item.path);

          return (
            <Link
              key={item.path}
              to={item.path}
              title={collapsed ? item.label : undefined}
              className={clsx(
                'relative flex items-center gap-3 rounded-lg transition-colors',
                collapsed ? 'justify-center p-2.5' : 'px-3 py-2',
                isActive
                  ? 'bg-primary text-textOnPrimary'
                  : 'text-textBody hover:bg-surfaceHover hover:text-textPrimary'
              )}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {!collapsed && <span className="text-sm">{item.label}</span>}
              {item.path === '/knowledge/review' && pendingReviews > 0 && (
                <span
                  title={`${pendingReviews} waiting for review`}
                  className={clsx(
                    'ml-auto min-w-[1.25rem] rounded-full bg-warning px-1.5 text-center text-xs font-medium text-textOnPrimary',
                    collapsed && 'absolute right-1 top-1 ml-0',
                  )}
                >
                  {pendingReviews}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Spacer */}
      <div className="flex-1" />

      {/* User menu */}
      <div className="border-t border-border">
        <div className={clsx('flex items-center', collapsed ? 'justify-center p-2' : 'justify-between px-3 py-2')}>
          {!collapsed && <span className="text-xs text-textSecondary">Theme</span>}
          <ThemeToggle />
        </div>
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className={clsx(
              'w-full flex items-center gap-2 text-textBody hover:bg-surfaceHover transition-colors',
              collapsed ? 'justify-center p-3' : 'px-3 py-2.5'
            )}
          >
            <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center text-textOnPrimary text-xs font-medium flex-shrink-0">
              {account?.name?.charAt(0)?.toUpperCase() || <User className="w-3.5 h-3.5" />}
            </div>
            {!collapsed && (
              <>
                <div className="flex-1 text-left min-w-0">
                  <div className="text-sm font-medium text-textPrimary truncate">
                    {account?.name || 'User'}
                  </div>
                </div>
                <ChevronDown className={clsx('w-3.5 h-3.5 transition-transform', showUserMenu && 'rotate-180')} />
              </>
            )}
          </button>

          {showUserMenu && (
            <div className={clsx(
              'absolute bottom-full bg-surfaceAlt border border-borderHover rounded-t-lg shadow-lg',
              collapsed ? 'left-full ml-2 bottom-0 rounded-lg w-48' : 'left-0 w-full'
            )}>
              <div className="flex items-center gap-3 px-4 py-2">
                <Shield className="w-4 h-4 text-textSecondary" />
                <span className={clsx('text-sm font-medium', ROLE_COLORS[role])}>{ROLE_LABELS[role]}</span>
              </div>
              {hasRole('poweruser') && (
                <Link
                  to="/profile"
                  onClick={() => setShowUserMenu(false)}
                  className="w-full flex items-center gap-3 px-4 py-2 text-textBody hover:bg-surfaceHover transition-colors"
                >
                  <User className="w-4 h-4" />
                  <span className="text-sm">Profile &amp; Docs</span>
                </Link>
              )}
              {/* Not role-gated — these are the signed-in user's own delivery preferences, and
                  the one place to turn the bell off has to be reachable by anyone who can
                  receive a notification. */}
              <Link
                to="/settings/notifications"
                onClick={() => setShowUserMenu(false)}
                className="w-full flex items-center gap-3 px-4 py-2 text-textBody hover:bg-surfaceHover transition-colors"
              >
                <Settings className="w-4 h-4" />
                <span className="text-sm">Notifications</span>
              </Link>
              {hasRole('creator') && (
                <a
                  href={`https://app.${window.location.hostname}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setShowUserMenu(false)}
                  className="w-full flex items-center gap-3 px-4 py-2 text-textBody hover:bg-surfaceHover transition-colors"
                >
                  <BookOpen className="w-4 h-4" />
                  <span className="text-sm">Journals</span>
                </a>
              )}
              <button
                onClick={() => {
                  setShowUserMenu(false);
                  logout();
                }}
                className="w-full flex items-center gap-3 px-4 py-2 text-error hover:bg-surfaceHover transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm">Sign out</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-bg">
      {/* Mobile hamburger */}
      <button
        onClick={() => setMobileOpen(true)}
        className="md:hidden fixed top-3 left-3 z-50 p-2 bg-surface border border-border rounded-lg text-textPrimary"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <aside
        className={clsx(
          'md:hidden fixed inset-y-0 left-0 z-50 w-64 bg-surface border-r border-border flex flex-col transform transition-transform duration-200',
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <button
          onClick={() => setMobileOpen(false)}
          className="absolute top-3 right-3 p-1 text-textSecondary hover:text-textPrimary"
        >
          <X className="w-5 h-5" />
        </button>
        {sidebarContent}
      </aside>

      {/* Desktop sidebar */}
      <aside
        className={clsx(
          'hidden md:flex flex-col bg-surface border-r border-border transition-all duration-200',
          collapsed ? 'w-16' : 'w-64'
        )}
      >
        {sidebarContent}
      </aside>

      {/* Main content + footer */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <main className="flex-1 min-h-0 overflow-auto md:ml-0 ml-0">
          <div className="md:hidden h-12" /> {/* Spacer for mobile hamburger */}
          {/* Above the page, not on a dashboard: the landing page is a chat REPL, so a reviewer
              who signs in to ask an agent something never walks past a queue. */}
          <ReviewAlert />
          {children}
        </main>
        <SproutFooter />
      </div>

      {/* Whisper floating assistant — hidden on chat pages to avoid overlapping the Send button */}
      {!location.pathname.includes('/chat') && (
        <Suspense fallback={null}>
          <WhisperWidget />
        </Suspense>
      )}

      {/* SDLC Capture surface — corner affordance + ⌘K, reachable from any page (KB 164 §4) */}
      <Suspense fallback={null}>
        <ReportAffordance />
      </Suspense>

      {/* SDLC "ping a human" — gate notifications, deep-linking into Track (KB 164 §6) */}
      <Suspense fallback={null}>
        <NotificationBell />
      </Suspense>

      {/* Mission toasts (KB 175 §6). Scoped to `subject="mission"` so it interrupts only for a
          mission finishing — report notifications keep going to the bell above, which is where
          the SDLC flow already expects them. */}
      <Suspense fallback={null}>
        <NotificationToasts subject="mission" />
      </Suspense>
    </div>
  );
}
